// Letters that don't decompose under NFKD but have a conventional ASCII
// equivalent. Mirror of SPECIAL_LETTER_MAP in backend/src/utils/slug.ts.
const SPECIAL_LETTER_MAP: Record<string, string> = {
	ß: 'ss', ẞ: 'SS',
	ø: 'o', Ø: 'O',
	æ: 'ae', Æ: 'AE',
	œ: 'oe', Œ: 'OE',
	ł: 'l', Ł: 'L',
	đ: 'd', Đ: 'D',
	þ: 'th', Þ: 'Th',
};

function transliterate(input: string): string {
	let out = '';
	for (const ch of input) {
		out += SPECIAL_LETTER_MAP[ch] ?? ch;
	}
	return out.normalize('NFKD').replace(/\p{M}+/gu, '');
}

/**
 * Derive a URL-safe slug from a display name.
 * - Transliterates Unicode letters (Czech/Polish/German/Hungarian/Nordic/…)
 *   via NFKD + special-letter pre-map instead of stripping them.
 * - Lowercased.
 * - Non-alphanumeric runs collapsed to single dash.
 * - Leading/trailing dashes trimmed.
 * - Capped at 64 characters.
 *
 * Mirrored in backend/src/utils/slug.ts. Keep both in sync.
 */
export function deriveSlug(name: string): string {
	return transliterate(name).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 64);
}

/**
 * Derive a lowercase, underscore-delimited key from a display name. Used for
 * `namesMap` keys on the project. Mirrored in `backend/src/utils/slug.ts`.
 */
export function deriveNamesMapKey(name: string): string {
	return transliterate(name).toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 64);
}

/**
 * Given a desired slug and the set of slugs already taken within the same
 * sibling scope, return a unique slug. If `desired` is free, return it as-is;
 * otherwise append `-2`, `-3`, … until an unused value is found.
 *
 * The caller is responsible for adding the returned value to `existing`
 * before calling again for the next sibling.
 */
export function makeUniqueSlug(desired: string, existing: Set<string> | ReadonlySet<string>): string {
	if (!existing.has(desired)) return desired;
	let i = 2;
	while (existing.has(`${desired}-${i}`)) i++;
	return `${desired}-${i}`;
}

/**
 * Generate a 24-character hex identifier compatible with MongoDB ObjectId
 * string form. Used to stamp entities with a stable `id` before a backend
 * round-trip. Crypto-random; collision risk is negligible for UI-generated
 * ids and we don't require monotonicity.
 */
export function generateEntityId(): string {
	const bytes = new Uint8Array(12);
	crypto.getRandomValues(bytes);
	return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * The project's own slug within the `company-campaign-project` composite the
 * backend composes (`buildProjectSlug`). Returns the whole slug when it
 * doesn't carry the prefix.
 *
 * The UI needs this because the user only ever sees and types the project
 * part: the Duplicate dialog prefills and validates against it, so comparing
 * against full stored slugs would never report a collision.
 * Backend counterpart: `splitProjectSlug` in
 * `services/project-duplicate.service.ts` (which also reports whether the
 * prefix was there — the API needs that, the UI doesn't).
 */
export function projectSlugPart(projectSlug: string, companySlug?: string, campaignSlug?: string): string {
	for (const prefix of [
		companySlug && campaignSlug ? `${companySlug}-${campaignSlug}-` : undefined,
		companySlug ? `${companySlug}-` : undefined,
	]) {
		if (prefix && projectSlug.startsWith(prefix)) return projectSlug.slice(prefix.length);
	}
	return projectSlug;
}
